import React from 'react';
import { useSite } from '../context/SiteContext';

interface ClinicLogoProps {
  variant?: 'full' | 'compact' | 'symbol';
  size?: 'sm' | 'md' | 'lg';
  isDark?: boolean; // If used on a dark background like the footer
  className?: string;
  customTitle?: string;
}

export const ClinicLogo: React.FC<ClinicLogoProps> = ({
  variant = 'full',
  size = 'md',
  isDark = false,
  className = '',
  customTitle,
}) => {
  let isFemale = false;
  let isApollon = false;
  let siteTitleFirst = 'BURSA';
  let siteTitleSecond = 'ERKEK LAZER';

  try {
    const site = useSite();
    if (site) {
      isFemale = site.isFemale;
      isApollon = site.isApollon;
      if (isApollon) {
        siteTitleFirst = 'APOLLON';
        siteTitleSecond = 'GÜZELLİK';
      } else if (isFemale) {
        siteTitleFirst = 'BURSA';
        siteTitleSecond = 'KADIN LAZER';
      } else {
        siteTitleFirst = 'BURSA';
        siteTitleSecond = 'ERKEK LAZER';
      }
    }
  } catch {
    // Fallback if rendered outside SiteProvider
  }

  if (customTitle) {
    const parts = customTitle.split(' ');
    if (parts.length > 1) {
      siteTitleFirst = parts[0];
      siteTitleSecond = parts.slice(1).join(' ');
    } else {
      siteTitleSecond = customTitle;
    }
  }

  const iconSize = size === 'sm' ? 'w-8 h-8' : size === 'lg' ? 'w-12 h-12' : 'w-8 h-8 sm:w-10 sm:h-10';
  const svgSize = size === 'sm' ? 'w-5 h-5' : size === 'lg' ? 'w-8 h-8' : 'w-5 h-5 sm:w-6 sm:h-6';
  const titleSize = size === 'sm' ? 'text-xs sm:text-base' : size === 'lg' ? 'text-lg sm:text-xl' : 'text-xs sm:text-base lg:text-lg';

  const getContainerBg = () => {
    if (isDark) {
      if (isApollon) return 'bg-gradient-to-br from-amber-300 via-amber-500 to-amber-600 shadow-amber-500/20';
      if (isFemale) return 'bg-gradient-to-br from-rose-400 via-rose-500 to-pink-600 shadow-rose-500/20';
      return 'bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 shadow-amber-500/20';
    }
    if (isApollon) return 'bg-gradient-to-br from-slate-900 via-amber-950 to-slate-950 border border-amber-600/40 shadow-amber-950/10';
    if (isFemale) return 'bg-gradient-to-br from-rose-950 via-rose-900 to-pink-950 border border-rose-800 shadow-rose-950/10';
    return 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-950 border border-slate-700 shadow-slate-900/10';
  };

  const getSubColor = () => {
    if (isDark) {
      if (isApollon) return 'text-amber-300 font-black';
      if (isFemale) return 'text-rose-400 font-black';
      return 'text-amber-400 font-black';
    }
    if (isApollon) return 'text-amber-600 font-black';
    if (isFemale) return 'text-rose-600 font-black';
    return 'text-amber-800 font-black';
  };

  const getSubtitle = () => {
    if (isApollon) return 'Medikal Estetik, Cilt Bakımı & Lazer Epilasyon';
    if (isFemale) return 'Kadınlara Özel Doktor Kliniği Medikal Epilasyon';
    return 'Doktor Kliniği Güvencesiyle Medikal Epilasyon';
  };

  return (
    <div className={`inline-flex items-center gap-2 sm:gap-3 ${className}`}>
      {/* Modern Medical Emblem */}
      <div className={`relative ${iconSize} shrink-0 flex items-center justify-center`}>
        <div className={`${iconSize} rounded-xl flex items-center justify-center shadow-sm transition-transform group-hover:scale-105 ${getContainerBg()}`}>
          <svg
            viewBox="0 0 40 40"
            className={svgSize}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="20" cy="20" r="14" fill={isDark ? '#0f172a' : (isFemale ? '#f43f5e' : '#f59e0b')} fillOpacity="0.15" />
            
            <path
              d="M20 7L24 16L33 20L24 24L20 33L16 24L7 20L16 16L20 7Z"
              fill={isDark ? '#0f172a' : (isFemale ? '#fb7185' : '#fbbf24')}
              stroke={isDark ? '#0f172a' : (isFemale ? '#e11d48' : '#f59e0b')}
              strokeWidth="0.8"
              strokeLinejoin="round"
            />
            
            <path
              d="M20 12L22.5 17.5L28 20L22.5 22.5L20 28L17.5 22.5L12 20L17.5 17.5L20 12Z"
              fill={isDark ? (isFemale ? '#fb7185' : '#f59e0b') : '#ffffff'}
            />

            <circle cx="20" cy="20" r="2.2" fill={isDark ? '#0f172a' : (isFemale ? '#be123c' : '#d97706')} />
          </svg>
        </div>

        <span className={`absolute -top-0.5 -right-0.5 w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full ${isFemale ? 'bg-rose-500' : isApollon ? 'bg-amber-400' : 'bg-emerald-500'} ring-2 ${isDark ? 'ring-slate-900' : 'ring-white'}`} />
      </div>

      {/* Typography */}
      {variant !== 'symbol' && (
        <div className="flex flex-col text-left justify-center min-w-0">
          <div className="flex items-center gap-1 sm:gap-2 flex-nowrap">
            <span
              className={`font-black ${titleSize} tracking-tight leading-tight whitespace-nowrap ${
                isDark ? 'text-white' : 'text-slate-900'
              }`}
            >
              {siteTitleFirst}{' '}
              <span className={getSubColor()}>
                {siteTitleSecond}
              </span>
            </span>
            <span
              className={`text-[9px] sm:text-[10px] font-bold px-1.5 py-0.5 rounded tracking-wide leading-tight border whitespace-nowrap shrink-0 hidden sm:inline-block ${
                isDark
                  ? (isFemale ? 'bg-rose-400/15 text-rose-300 border-rose-400/30' : isApollon ? 'bg-amber-400/15 text-amber-300 border-amber-400/30' : 'bg-amber-400/15 text-amber-300 border-amber-400/30')
                  : (isFemale ? 'bg-rose-50 text-rose-800 border-rose-300/80 shadow-2xs' : isApollon ? 'bg-amber-50 text-amber-800 border-amber-300/80 shadow-2xs' : 'bg-amber-50 text-amber-800 border-amber-300/80 shadow-2xs')
              }`}
            >
              FSM BULVARI
            </span>
          </div>

          {variant === 'full' && (
            <p
              className={`text-[10px] sm:text-[11px] font-medium tracking-normal mt-0.5 leading-snug truncate max-w-[200px] sm:max-w-none hidden md:block ${
                isDark ? 'text-slate-400' : 'text-slate-500'
              }`}
            >
              {getSubtitle()}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
